import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { DeltaPill } from '../../../components/primitives/DeltaPill';
import { Panel } from '../../../components/primitives/Panel';
import { AcceleratorSummary } from '../../periodSections';
import { changePctOf, formatChangePct, formatCompact, formatMonth } from '../../streamingFormat';
import { acceleratorMessages as messages } from '../../acceleratorMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import shared from '../../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the period sections; each part uses its own slice of it */
import styles from '../../StreamingPeriod.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface AcceleratorTilesProps {
  summary: AcceleratorSummary;
  trackCount: number;
}

function percent(intl: ReturnType<typeof useIntl>, share: number | null): string {
  return share === null
    ? '—'
    : intl.formatNumber(share, { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/**
 * The four tiles of Accelerator (design streaming-sections d1). Accelerator streams are part of the
 * TuneCore total: they are never added to Streams. A number that TuneCore did not give is a dash.
 */
const AcceleratorTilesComponent: React.FC<AcceleratorTilesProps> = ({ summary, trackCount }) => {
  const intl = useIntl();
  const { newest, beforeNewest } = summary;
  const changePct =
    newest === null ? null : changePctOf(newest.acceleratorStreams, beforeNewest?.acceleratorStreams ?? null);
  const change = formatChangePct(intl, changePct);
  const newestOverall = newest?.overallStreams ?? null;
  const newestShare =
    newest !== null && newestOverall !== null && newestOverall > 0 ? newest.acceleratorStreams / newestOverall : null;
  // A null count of promoted tracks is a dash; with no month the table rows are the count.
  const promotedTracks = newest === null ? trackCount : newest.promotedTracks;

  return (
    <div className={clsx(styles.kpis, styles.kpisFour)} data-testid="streaming-accelerator-tiles">
      <Panel className={shared.tile}>
        <span className={shared.tileLabel}>{intl.formatMessage(messages.fromAccelerator)}</span>
        <span className={shared.tileValue}>{formatCompact(intl, summary.acceleratorStreams)}</span>
      </Panel>
      <Panel className={shared.tile}>
        <span className={shared.tileLabel}>{intl.formatMessage(messages.shareOfAll)}</span>
        <span className={shared.tileValue}>{percent(intl, summary.share)}</span>
        <span className={shared.faint}>
          {intl.formatMessage(messages.shareOfAllSub, { count: formatCompact(intl, summary.overallStreams) })}
        </span>
      </Panel>
      {newest !== null ? (
        <Panel className={shared.tile}>
          <span className={shared.tileLabel}>{formatMonth(intl, newest.month)}</span>
          <span className={shared.tileValue}>{formatCompact(intl, newest.acceleratorStreams)}</span>
          <span className={shared.tileRow}>
            {change !== null && changePct !== null ? <DeltaPill value={changePct} label={change} /> : null}
            <span className={shared.faint}>
              {intl.formatMessage(messages.monthSub, {
                share: percent(intl, newestShare),
                count: formatCompact(intl, newest.overallStreams),
              })}
            </span>
          </span>
        </Panel>
      ) : null}
      <Panel className={shared.tile}>
        <span className={shared.tileLabel}>{intl.formatMessage(messages.tracksCount)}</span>
        <span className={shared.tileValue}>{formatCompact(intl, promotedTracks)}</span>
      </Panel>
    </div>
  );
};

export const AcceleratorTiles = TypedMemo(AcceleratorTilesComponent);
